/**
 * The three curated peptide overlays: which sets exist, which hides which, and where their
 * coordinates are cached (`V150`, `V152`, P31).
 *
 * ## READ FIRST — this module is deliberately specific
 *
 * Every other control in this app is built to `V18`: **dispatch on a column's declared *role*,
 * never on its name**, so a new label is a field in a manifest rather than a branch in the code.
 * The colour select, the landmark select and the health panel all obey it, which is why none of
 * them needed editing when an atlas gained a column.
 *
 * **`CURATED_ORDER` and `HIDDEN_BY` below break that rule on purpose.** Three named sets, and a
 * precedence rule that is a fact about two particular files rather than something either file
 * declares. It is written this way because three cases do not pay for a declaration format, and
 * because the captions are domain sentences that no manifest field could supply.
 *
 * **Do not copy this pattern.** `.claude/workspace/PLAN_CURATED_SETS.md §13` records what the
 * general form is — N declared point sets, each carrying its own caption, colour and precedence —
 * and names the trigger for building it: **a fourth set**. Three hardcoded rows is a table; four
 * is a pattern being copied, and that is when the declaration format repays its cost.
 *
 * ## A cache, where `encoded.ts` is a record — and every rule inverts
 *
 * `encoded.ts` holds sequences the reader typed, and its three governing rules all come from that.
 * None of them applies here, and sharing its store would have been wrong in three separate ways:
 *
 * | | `encoded.ts` | this |
 * |---|---|---|
 * | what cannot be recreated | the typing (`V138`) | **nothing** — the lists come from `GET /curated` |
 * | a record of another build | kept, listed, re-placeable (`V139`) | **discarded**, silently, and refetched |
 * | the 500-per-view cap | refuses rather than evicts, to protect typing (`V142`) | **does not apply** — 206 curated points must not consume the reader's slots |
 *
 * So this stores the *derived* half only: `sequence -> xy`, per view. A cache entry is ~80 bytes
 * where an `EncodedMark` is ~503, which is what makes holding several views affordable.
 *
 * ## Why it is bounded by views rather than by points
 *
 * A coordinate belongs to one `(atlas, projection, build)`, and `raw:<i>,<j>` is a valid
 * projection for **any** dimension pair — 32,640 of them at `latent_dim = 256`. A reader sweeping
 * raw views would otherwise grow this key without limit. `MAX_VIEWS` bounds it; eviction is
 * oldest-first and costs one `/encode_batch` to undo.
 */
import type { CuratedSet } from './api';

/** `v1` in the key, so a shape change is a new key rather than a parse of a stale record. */
export const STORAGE_KEY = 'peptide-vis.curated.v1';

/**
 * How many `(atlas, projection, build)` frames keep their coordinates.
 *
 * At 206 sequences a view is ~16 kB, so eight views is ~130 kB against a ~5 MB origin quota
 * **shared with `decoded.ts` and `encoded.ts`** — small enough not to compete with the two stores
 * that hold things a reader cannot get back. Eight is more projections than any atlas here
 * declares, so it binds only on a raw-dimension sweep, which is exactly the case it exists for.
 */
export const MAX_VIEWS = 8;

/**
 * The sets, in display order. **The specific part** — see this module's header.
 *
 * The ids match `src/peptide_vis/curated.py`'s `CURATED_SETS`, and a set the server does not serve
 * simply does not appear: the frontend renders what `GET /curated` returned, filtered through this
 * order, so removing a file from the serving machine removes a button rather than breaking one.
 */
export const CURATED_ORDER = ['thanatin', 'candidates', 'soonest'] as const;

export type CuratedId = (typeof CURATED_ORDER)[number];

/** Whether a set is off, drawn, or drawn with a label beside every point. */
export type SetView = 'off' | 'on' | 'on+landmarks';

export const isShown = (view: SetView): boolean => view !== 'off';
export const hasLandmarks = (view: SetView): boolean => view === 'on+landmarks';

/**
 * The one precedence rule: **`soonest` hides `candidates`** (`V152`).
 *
 * `candidates_2` is wholly contained in `candidates` — all 46 of its sequences appear among the
 * 200 — so with both shown, every soonest point would otherwise draw twice, once in each colour,
 * and which one the reader saw would depend on draw order. The reader's instruction is to show the
 * finer level, so a sequence in both draws once, as `soonest`, and `candidates` draws its 154
 * remaining members.
 *
 * **Nowhere else, deliberately.** The thanatin panel shares one sequence with `candidates`
 * (`analogue_6` is the same peptide as candidate `7`) and two pairs with itself (`analogue_4` with
 * `analogue_3`, `analogue_7` with `analogue_6`, which the vocabulary substitutions made identical).
 * All of those **draw coincident, twice**, because they are different compounds that this
 * checkpoint cannot tell apart and collapsing them would answer a question the reader has already
 * answered the other way — which is also why `thanatin_panel_cleaned.csv` is not deduplicated.
 *
 * A map from the hidden set to the set that hides it, so the lookup is by the set being drawn.
 */
export const HIDDEN_BY: Partial<Record<CuratedId, CuratedId>> = { candidates: 'soonest' };

/** What the map draws for one curated peptide. */
export interface CuratedGlyph {
  readonly set: CuratedId;
  /** Unique per glyph, and **stable across renders** so an overlay node can be reused. */
  readonly id: string;
  readonly sequence: string;
  /** The row's own label, in full: `analogue_6 [Hyp>Pro Pen>LEI]`. */
  readonly label: string;
  /** The label up to its first bracket — what a landmark prints. See `shortLabel`. */
  readonly short: string;
  readonly xy: [number, number];
}

/**
 * The part of a label that names the compound, without the substitution note.
 *
 * A curated label carries what was replaced to make the sequence encodable —
 * `analogue_6 [Hyp>Pro Pen>LEI] (+1 merged)` — and that belongs on the card, where there is room
 * for it. A landmark is text drawn beside a 2 px dot, and two hundred of them are on screen at
 * once, so it prints `analogue_6` and stops. Nothing is lost: the full label is one hover away.
 */
export const shortLabel = (label: string): string => {
  const cut = label.search(/\s[[(]/);
  return (cut === -1 ? label : label.slice(0, cut)).trim();
};

export type SetViews = Readonly<Record<CuratedId, SetView>>;

export const ALL_OFF: SetViews = { thanatin: 'off', candidates: 'off', soonest: 'off' };

/**
 * Every sequence the shown sets need a coordinate for, distinct, in a stable order.
 *
 * **Computed after precedence**, so a sequence that only `candidates` carries and `soonest` hides
 * is not fetched. The order is `CURATED_ORDER` then row order, which makes the request stable
 * between renders and therefore the cache lookups stable too.
 */
export function neededSequences(sets: readonly CuratedSet[], views: SetViews): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const set of ordered(sets)) {
    if (!isShown(views[set.id])) continue;
    const hider = HIDDEN_BY[set.id];
    const hidden = hider !== undefined && isShown(views[hider]) ? sequencesOf(sets, hider) : null;
    for (const row of set.rows) {
      if (hidden !== null && hidden.has(row.sequence)) continue;
      if (seen.has(row.sequence)) continue;
      seen.add(row.sequence);
      out.push(row.sequence);
    }
  }
  return out;
}

/**
 * The glyphs to draw, precedence applied, in `CURATED_ORDER`.
 *
 * **Resolved once into a flat list**, rather than decided per glyph while drawing. Deciding at
 * draw time is how the same point ends up drawn twice on one frame and once on the next, because
 * the two passes see different halves of the state.
 *
 * A row whose coordinate is not in `coords` is **skipped, not drawn at the origin**. A missing
 * coordinate means the fetch has not landed or the batch refused, and drawing it at `(0, 0)` is
 * `V115`'s failure — a mark placed where the data never was.
 */
export function resolveGlyphs(
  sets: readonly CuratedSet[],
  views: SetViews,
  coords: ReadonlyMap<string, [number, number]>,
): CuratedGlyph[] {
  const out: CuratedGlyph[] = [];
  for (const set of ordered(sets)) {
    if (!isShown(views[set.id])) continue;
    const hider = HIDDEN_BY[set.id];
    const hidden = hider !== undefined && isShown(views[hider]) ? sequencesOf(sets, hider) : null;
    set.rows.forEach((row, at) => {
      if (hidden !== null && hidden.has(row.sequence)) return;
      const xy = coords.get(row.sequence);
      if (xy === undefined) return;
      out.push({
        set: set.id,
        // The row index, not the sequence: the panel carries nine rows over seven distinct
        // sequences on purpose, and two glyphs keyed by sequence would be one glyph.
        id: `${set.id}:${at}`,
        sequence: row.sequence,
        label: row.label,
        short: shortLabel(row.label),
        xy,
      });
    });
  }
  return out;
}

/** The served sets that this build knows about, in display order. */
function ordered(sets: readonly CuratedSet[]): { id: CuratedId; rows: CuratedSet['rows'] }[] {
  const byId = new Map(sets.map((s) => [s.id, s]));
  return CURATED_ORDER.flatMap((id) => {
    const set = byId.get(id);
    return set === undefined ? [] : [{ id, rows: set.rows }];
  });
}

function sequencesOf(sets: readonly CuratedSet[], id: CuratedId): Set<string> {
  const set = sets.find((s) => s.id === id);
  return new Set(set === undefined ? [] : set.rows.map((r) => r.sequence));
}

// -- the coordinate cache ----------------------------------------------------------------------

/** What makes two coordinates comparable: one projection, of one build, of one atlas. */
export interface CuratedView {
  atlasId: string;
  projection: string;
  builtAt: string;
}

const viewKey = (view: CuratedView): string =>
  `${view.atlasId} ${view.projection} ${view.builtAt}`;

export interface CuratedCache {
  /** The coordinates this view already has, as the map `resolveGlyphs` takes. */
  forView(view: CuratedView): Map<string, [number, number]>;
  /** Which of `sequences` this view has no coordinate for, in the order given. */
  missing(view: CuratedView, sequences: readonly string[]): string[];
  /** Record a batch. Same length and order as the request, which the route guarantees. */
  put(view: CuratedView, sequences: readonly string[], xy: readonly [number, number][]): void;
  /** Drop everything. The recovery when a payload cannot be parsed at all. */
  clear(): void;
}

function defaultStorage(): Storage | null {
  // The property access itself throws under some privacy settings, so this is a `try` rather than
  // a null check — `encoded.ts`'s reason, and the same code.
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

const isXY = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  typeof value[0] === 'number' &&
  typeof value[1] === 'number' &&
  Number.isFinite(value[0]) &&
  Number.isFinite(value[1]);

/** `{ viewKey: { sequence: [x, y] } }`, insertion-ordered so eviction can be oldest-first. */
type Payload = Record<string, Record<string, [number, number]>>;

function isPayload(value: unknown): value is Payload {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      !Array.isArray(entry) &&
      Object.values(entry as Record<string, unknown>).every(isXY),
  );
}

/**
 * The cache, over an injectable `Storage`.
 *
 * The parameter is what makes this unit-testable, exactly as in `encoded.ts`: the tests pass a
 * stub that can be made to throw on `setItem`. `null` gives a cache that keeps nothing for the
 * life of the page, which here costs one `/encode_batch` per reload and nothing else — the reason
 * this module can be so much more relaxed about storage than its two twins.
 */
export function createCuratedCache(storage: Storage | null = defaultStorage()): CuratedCache {
  let persisting = storage !== null;
  let volatilePayload: Payload = {};

  function read(): Payload {
    if (!persisting || storage === null) return { ...volatilePayload };
    let raw: string | null = null;
    try {
      raw = storage.getItem(STORAGE_KEY);
    } catch {
      persisting = false;
      return { ...volatilePayload };
    }
    if (raw === null) return {};
    try {
      const parsed: unknown = JSON.parse(raw);
      // **Whole-payload validation, unlike `encoded.ts`'s per-record rule.** There it costs the
      // reader a typed peptide to discard one; here it costs one request, so the simpler rule is
      // the right one.
      return isPayload(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  function commit(next: Payload): void {
    if (persisting && storage !== null) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(next));
        return;
      } catch {
        // **No latch back to storage and no error to the caller.** A refused write here means the
        // coordinates are recomputed next reload; nothing a reader did is lost.
        persisting = false;
      }
    }
    volatilePayload = next;
  }

  return {
    forView(view) {
      return new Map(Object.entries(read()[viewKey(view)] ?? {}));
    },

    missing(view, sequences) {
      const have = read()[viewKey(view)] ?? {};
      const asked = new Set<string>();
      return sequences.filter((s) => {
        if (s in have || asked.has(s)) return false;
        asked.add(s);
        return true;
      });
    },

    put(view, sequences, xy) {
      if (sequences.length === 0) return;
      const key = viewKey(view);
      const payload = read();
      const entry = { ...(payload[key] ?? {}) };
      sequences.forEach((sequence, at) => {
        const point = xy[at];
        if (point !== undefined) entry[sequence] = point;
      });
      // Re-inserting the key moves it to the end of the insertion order, which is what makes
      // eviction below oldest-*touched* rather than oldest-written.
      delete payload[key];
      payload[key] = entry;
      const keys = Object.keys(payload);
      for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_VIEWS))) {
        delete payload[stale];
      }
      commit(payload);
    },

    clear() {
      commit({});
    },
  };
}
